// C:\Ava_Local\Ava_API\avaai_dashboard\frontend\src\pages\education\TutorPage.tsx
import React, { useContext, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ThemeContext } from "../../context/ThemeContext";

export type TutorTopic =
  | "Addition"
  | "Subtraction"
  | "Multiplication"
  | "Division"
  | "Fractions"
  | "Functions"
  | "Decimals"
  | "Essay Writing";

type GradeBand = "K-2" | "3-5" | "6-8" | "9-12";

type SolveResult = {
  ok: boolean;
  normalized?: string;
  answer?: number | string;
  steps?: string[];
  hint?: string;
  easier?: string;
  example?: string;
  friendly?: string;
  error?: string;
};

type TutorPageProps = {
  initialTopic?: TutorTopic;
  lockTopic?: boolean;
};

function makeTutorStyles(themeColors: any) {
  const pageStyle: React.CSSProperties = {
    minHeight: "100vh",
    width: "100%",
    boxSizing: "border-box",
    padding: "16px 12px 16px",
    background: themeColors.bg,
    color: themeColors.text,
  };

  const shellStyle: React.CSSProperties = {
    maxWidth: "1400px",
    margin: "0 auto",
  };

  const panelStyle: React.CSSProperties = {
    borderRadius: "20px",
    padding: "16px 16px",
    background: themeColors.panel,
    border: `1px solid ${themeColors.border}`,
    boxShadow: themeColors.glow,
    backdropFilter: "blur(10px)",
  };

  const cardStyle: React.CSSProperties = {
    borderRadius: "16px",
    padding: "14px",
    background: themeColors.cardBg,
    color: themeColors.text,
    border: `1px solid ${themeColors.border}`,
    boxShadow:
      "0 8px 22px rgba(0,0,0,0.26), inset 0 1px 0 rgba(255,255,255,0.03)",
  };

  const controlStyle: React.CSSProperties = {
    background: themeColors.panel,
    color: themeColors.text,
    border: `1px solid ${themeColors.border}`,
    borderRadius: "12px",
    padding: "10px 12px",
    fontSize: "15px",
    outline: "none",
    minHeight: "44px",
    width: "100%",
    boxSizing: "border-box",
  };

  const buttonStyle: React.CSSProperties = {
    minHeight: "44px",
    borderRadius: "12px",
    border: `1px solid ${themeColors.border}`,
    background: themeColors.buttonBg,
    color: themeColors.text,
    fontSize: "14px",
    fontWeight: 800,
    cursor: "pointer",
    padding: "0 14px",
    boxShadow: themeColors.glow,
  };

  return {
    pageStyle,
    shellStyle,
    panelStyle,
    cardStyle,
    controlStyle,
    buttonStyle,
  };
}

function topicToSymbol(topic: TutorTopic): string {
  switch (topic) {
    case "Addition":
      return "+";
    case "Subtraction":
      return "-";
    case "Multiplication":
      return "×";
    case "Division":
      return "÷";
    case "Fractions":
      return "½";
    case "Functions":
      return "ƒ";
  }

  return "";
}

function safeNumber(value: string): number | null {
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function normalizeInput(raw: string, topic: TutorTopic): string {
  let text = raw.trim();
  if (!text) return "";

  text = text
    .replace(/plus/gi, "+")
    .replace(/minus/gi, "-")
    .replace(/times/gi, "×")
    .replace(/x/gi, "×")
    .replace(/\*/g, "×")
    .replace(/divided by/gi, "÷")
    .replace(/\//g, "÷");

  if (topic === "Fractions" || topic === "Functions") {
    return raw.trim().replace(/\s+/g, " ");
  }

  const hasOperator = /[+\-×÷]/.test(text);
  if (!hasOperator) {
    const nums = text.match(/-?\d+(\.\d+)?/g) || [];
    if (nums.length >= 2) {
      text = `${nums[0]} ${topicToSymbol(topic)} ${nums[1]}`;
    }
  }

  return text.replace(/\s+/g, " ").trim();
}

function gradeFriendlyLine(grade: GradeBand): string {
  switch (grade) {
    case "K-2":
      return "We will keep it simple and take one small step at a time.";
    case "3-5":
      return "We can break it into clear parts so it feels easier.";
    case "6-8":
      return "We will use a structured step-by-step method.";
    case "9-12":
      return "We will solve it clearly and show the reasoning.";
  }
  return "";
}

function buildExample(topic: TutorTopic): string {
  switch (topic) {
    case "Addition":
      return "Example: 27 + 15\nStep 1: Add the ones: 7 + 5 = 12\nStep 2: Write 2 and carry 1\nStep 3: Add the tens: 2 + 1 + 1 = 4\nAnswer: 42";
    case "Subtraction":
      return "Example: 42 - 17\nStep 1: Subtract 10 first: 42 - 10 = 32\nStep 2: Subtract 7: 32 - 7 = 25\nAnswer: 25";
    case "Multiplication":
      return "Example: 4 × 6\nStep 1: Think of 4 groups of 6\nStep 2: 6 + 6 + 6 + 6 = 24\nAnswer: 24";
    case "Division":
      return "Example: 24 ÷ 6\nStep 1: Ask how many groups of 6 fit into 24\nStep 2: 6 + 6 + 6 + 6 = 24\nAnswer: 4";
    case "Fractions":
      return "Example: 1/2 + 1/4\nStep 1: Make the denominators match\nStep 2: 1/2 = 2/4\nStep 3: 2/4 + 1/4 = 3/4\nAnswer: 3/4";
    case "Functions":
      return "Example: f(x) = 2x + 3, find f(4)\nStep 1: Replace x with 4\nStep 2: f(4) = 2(4) + 3\nStep 3: 8 + 3 = 11\nAnswer: 11";
  }

  return "";
}

function solveFractionProblem(raw: string, grade: GradeBand): SolveResult {
  const normalized = raw.replace(/\s+/g, "");

  const addSubMatch = normalized.match(/^(\d+)\/(\d+)([\+\-])(\d+)\/(\d+)$/);
  if (addSubMatch) {
    const n1 = Number(addSubMatch[1]);
    const d1 = Number(addSubMatch[2]);
    const op = addSubMatch[3];
    const n2 = Number(addSubMatch[4]);
    const d2 = Number(addSubMatch[5]);

    if (d1 === 0 || d2 === 0) {
      return { ok: false, error: "A fraction cannot have 0 as the denominator." };
    }

    const common = d1 * d2;
    const left = n1 * d2;
    const right = n2 * d1;
    const top = op === "+" ? left + right : left - right;

    return {
      ok: true,
      normalized: raw,
      answer: `${top}/${common}`,
      friendly: gradeFriendlyLine(grade),
      hint: "Try turning both fractions into matching denominator fractions first.",
      easier: "1/2 + 1/2",
      example: buildExample("Fractions"),
      steps: [
        `Problem: ${n1}/${d1} ${op} ${n2}/${d2}`,
        `Step 1: Find a common denominator. ${d1} × ${d2} = ${common}`,
        `Step 2: Rewrite the fractions: ${n1}/${d1} = ${left}/${common} and ${n2}/${d2} = ${right}/${common}`,
        `Step 3: ${left}/${common} ${op} ${right}/${common} = ${top}/${common}`,
        `Answer: ${top}/${common}`,
      ],
    };
  }

  const simpleMatch = normalized.match(/^(\d+)\/(\d+)$/);
  if (simpleMatch) {
    const n = Number(simpleMatch[1]);
    const d = Number(simpleMatch[2]);

    if (d === 0) {
      return { ok: false, error: "A fraction cannot have 0 as the denominator." };
    }

    return {
      ok: true,
      normalized: raw,
      answer: `${n}/${d}`,
      friendly: gradeFriendlyLine(grade),
      hint: "The top number is the numerator and the bottom number is the denominator.",
      easier: "1/2",
      example: buildExample("Fractions"),
      steps: [
        `Problem: ${n}/${d}`,
        `Step 1: The numerator is ${n}.`,
        `Step 2: The denominator is ${d}.`,
        `Step 3: This fraction means ${n} part(s) out of ${d} equal part(s).`,
        `Answer: ${n}/${d}`,
      ],
    };
  }

  return {
    ok: false,
    error: "Try a fraction problem like 1/2 + 1/4, 3/5 - 1/5, or just 3/4.",
  };
}

function solveFunctionProblem(raw: string, grade: GradeBand): SolveResult {
  const compact = raw.replace(/\s+/g, "");

  const evalMatch = compact.match(/^f\(x\)=(-?\d*)x([+\-]\d+)?[,;:]?f\((-?\d+)\)$/i);
  if (evalMatch) {
    const aRaw = evalMatch[1];
    const bRaw = evalMatch[2] || "0";
    const xValue = Number(evalMatch[3]);

    const a =
      aRaw === "" || aRaw === "+"
        ? 1
        : aRaw === "-"
        ? -1
        : Number(aRaw);
    const b = Number(bRaw);
    const answer = a * xValue + b;

    return {
      ok: true,
      normalized: raw,
      answer,
      friendly: gradeFriendlyLine(grade),
      hint: "Replace x with the given number, then simplify.",
      easier: "f(x) = x + 2, f(3)",
      example: buildExample("Functions"),
      steps: [
        `Problem: f(x) = ${a}x ${b >= 0 ? `+ ${b}` : `- ${Math.abs(b)}`}, find f(${xValue})`,
        `Step 1: Replace x with ${xValue}.`,
        `Step 2: f(${xValue}) = ${a}(${xValue}) ${b >= 0 ? `+ ${b}` : `- ${Math.abs(b)}`}`,
        `Step 3: ${a * xValue} ${b >= 0 ? `+ ${b}` : `- ${Math.abs(b)}`} = ${answer}`,
        `Answer: ${answer}`,
      ],
    };
  }

  const simpleMatch = compact.match(/^y=(-?\d*)x([+\-]\d+)?$/i);
  if (simpleMatch) {
    const aRaw = simpleMatch[1];
    const bRaw = simpleMatch[2] || "0";
    const a =
      aRaw === "" || aRaw === "+"
        ? 1
        : aRaw === "-"
        ? -1
        : Number(aRaw);
    const b = Number(bRaw);

    return {
      ok: true,
      normalized: raw,
      answer: `slope=${a}, intercept=${b}`,
      friendly: gradeFriendlyLine(grade),
      hint: "In y = mx + b, m is the slope and b is the y-intercept.",
      easier: "y = x + 2",
      example: buildExample("Functions"),
      steps: [
        `Problem: y = ${a}x ${b >= 0 ? `+ ${b}` : `- ${Math.abs(b)}`}`,
        `Step 1: Compare it to y = mx + b.`,
        `Step 2: The slope m is ${a}.`,
        `Step 3: The y-intercept b is ${b}.`,
        `Answer: slope=${a}, intercept=${b}`,
      ],
    };
  }

  return {
    ok: false,
    error: "Try a function problem like f(x) = 2x + 3, f(4) or y = 3x - 2.",
  };
}


function solveEssayProblem(raw: string, grade: GradeBand): SolveResult {
  const clean = raw.trim();

  if (!clean) {
    return {
      ok: false,
      error: "Type a paragraph, essay topic, or writing question first.",
    };
  }

  const lower = clean.toLowerCase();
  const isFix = /\b(fix|edit|grammar|correct|rewrite|improve)\b/.test(lower);
  const isBrainstorm = /\b(idea|ideas|brainstorm|topic|topics)\b/.test(lower);
  const isIntro = /\b(introduction|intro|start|opening)\b/.test(lower);
  const isConclusion = /\b(conclusion|ending|end)\b/.test(lower);

  let focus = "Essay plan";
  let steps = [
    "Step 1: Choose one clear main idea.",
    "Step 2: Turn that main idea into a thesis statement.",
    "Step 3: Add body paragraphs with evidence or examples.",
    "Step 4: End with a conclusion that restates the main idea.",
    "Step 5: Reread and fix spelling, punctuation, and sentence clarity.",
  ];

  if (isFix) {
    focus = "Editing help";
    steps = [
      "Step 1: Read the paragraph out loud.",
      "Step 2: Fix spelling and punctuation first.",
      "Step 3: Make each sentence clear and complete.",
      "Step 4: Remove repeated words or confusing phrases.",
      "Step 5: Check that the paragraph still says what you meant.",
    ];
  } else if (isBrainstorm) {
    focus = "Brainstorming help";
    steps = [
      "Step 1: List 3 possible ideas.",
      "Step 2: Pick the idea you can explain best.",
      "Step 3: Write one sentence saying your opinion or main point.",
      "Step 4: Add 2 or 3 reasons to support it.",
      "Step 5: Turn those reasons into body paragraphs.",
    ];
  } else if (isIntro) {
    focus = "Introduction help";
    steps = [
      "Step 1: Start with a hook or interesting first sentence.",
      "Step 2: Give a little background.",
      "Step 3: End the introduction with your thesis statement.",
    ];
  } else if (isConclusion) {
    focus = "Conclusion help";
    steps = [
      "Step 1: Restate your thesis in new words.",
      "Step 2: Summarize your strongest reason.",
      "Step 3: End with a final thought that feels complete.",
    ];
  }

  return {
    ok: true,
    normalized: clean,
    answer: focus,
    friendly: gradeFriendlyLine(grade),
    hint: "A strong essay needs a clear main idea, organized paragraphs, and careful editing.",
    easier: "Write 3 sentences: your main idea, one reason, and one closing sentence.",
    example: buildExample("Essay Writing"),
    steps,
  };
}

function solveDecimalProblem(raw: string, grade: GradeBand): SolveResult {
  const compareMatch = raw.trim().match(/compare\s+(-?\d+(?:\.\d+)?)\s+(?:and|to)\s+(-?\d+(?:\.\d+)?)/i);
  if (compareMatch) {
    const a = Number(compareMatch[1]);
    const b = Number(compareMatch[2]);
    const relation = a === b ? "=" : a > b ? ">" : "<";

    return {
      ok: true,
      normalized: `Compare ${a} and ${b}`,
      answer: `${a} ${relation} ${b}`,
      friendly: gradeFriendlyLine(grade),
      hint: "Compare decimals by lining up the decimal points and checking place value.",
      easier: "Compare 0.5 and 0.05",
      example: buildExample("Decimals"),
      steps: [
        `Problem: Compare ${a} and ${b}`,
        "Step 1: Line up the decimal points.",
        "Step 2: Compare tenths, then hundredths, then thousandths.",
        `Answer: ${a} ${relation} ${b}`,
      ],
    };
  }

  const normalized = normalizeInput(raw, "Addition");
  const match = normalized.match(/(-?\d+(?:\.\d+)?)\s*([+\-×÷])\s*(-?\d+(?:\.\d+)?)/);

  if (!match) {
    return {
      ok: false,
      error: "Try a decimal problem like 3.25 + 1.7, 0.5 × 4, compare 0.5 and 0.05, or 12.6 ÷ 3.",
    };
  }

  const a = Number(match[1]);
  const op = match[2];
  const b = Number(match[3]);

  if (op === "÷" && b === 0) {
    return { ok: false, error: "Division by zero is not allowed. Try a different divisor." };
  }

  let answer = 0;
  if (op === "+") answer = a + b;
  if (op === "-") answer = a - b;
  if (op === "×") answer = a * b;
  if (op === "÷") answer = a / b;

  return {
    ok: true,
    normalized,
    answer,
    friendly: gradeFriendlyLine(grade),
    hint: op === "+" || op === "-"
      ? "Line up the decimal points before adding or subtracting."
      : "Multiply or divide carefully, then check where the decimal belongs.",
    easier: "0.5 + 0.2",
    example: buildExample("Decimals"),
    steps: [
      `Problem: ${a} ${op} ${b}`,
      op === "+" || op === "-" ? "Step 1: Line up the decimal points." : "Step 1: Treat the numbers carefully and track decimal places.",
      "Step 2: Solve from right to left.",
      op === "+" || op === "-" ? "Step 3: Bring the decimal point straight down." : "Step 3: Place the decimal in the correct spot.",
      `Answer: ${answer}`,
    ],
  };
}


// === AVAAIO EDUCATION TUTOR SAFETY BOUNDARY V1.1 ===

function tutorEducationSafetyError(raw: string): string | null {
  const value = String(raw || "").trim().toLowerCase();

  if (!value) return null;

  const instructional =
    /\b(how\s+to|how\s+(do|can|could|would|should)\s+i|teach\s+me\s+(how\s+)?to|show\s+me\s+(how\s+)?to|give\s+me\s+(the\s+)?(steps|instructions|method|methods|guide)|step[- ]by[- ]step|best\s+way\s+to|easiest\s+way\s+to|ways?\s+to|help\s+me\s+(make|build|create|do|break|hack|hurt|kill|steal|hide))\b/.test(
      value
    );

  const explicitSexual =
    /\b(porn|pornography|nudes?|sexting|explicit\s+sex(ual)?|sex\s+positions?|sexual\s+acts?|sexual\s+intercourse|masturbat(e|ion)|oral\s+sex)\b/.test(
      value
    );

  if (explicitSexual) {
    return (
      "I can't provide explicit sexual content or sexual instructions in AVAAIO Education. " +
      "Please choose another school-safe topic."
    );
  }

  const dangerousWeapon =
    /\b(bomb|bombs|explosive|explosives|detonator|pipe\s+bomb|molotov|improvised\s+explosive|\bied\b|firearm|firearms|weapon|weapons)\b/.test(
      value
    );

  if (dangerousWeapon && instructional) {
    return (
      "I can't provide instructions for making or using bombs, explosives, weapons, " +
      "or other dangerous devices. I can help with safe science, history, prevention, " +
      "or general safety education instead."
    );
  }

  const drugSubject =
    /\b(meth|methamphetamine|cocaine|crack|heroin|fentanyl|illegal\s+drugs?|recreational\s+drugs?|get\s+high|drug\s+dealing)\b/.test(
      value
    );

  const drugAction =
    /\b(make|cook|manufacture|synthesize|produce|inject|snort|smoke|sell|deal|distribute|hide|conceal)\b/.test(
      value
    );

  if (drugSubject && (instructional || drugAction)) {
    return (
      "I can't provide instructions for making, using, selling, or hiding illegal drugs. " +
      "I can explain health risks, prevention, or addiction science safely."
    );
  }

  const intrusion =
    /\b(hack(ing)?|phish(ing)?|malware|ransomware|keylogger|steal\s+(a\s+)?password|steal\s+credentials?|bypass\s+(a\s+)?(password|login|security)|break\s+into\s+(an?\s+)?(account|computer|network|server)|pick\s+(a\s+)?lock|lockpick(ing)?|break\s+into\s+(a\s+)?(house|home|car|vehicle|building)|hotwire|disable\s+(an?\s+)?alarm)\b/.test(
      value
    );

  const defensiveSecurity =
    /\b(cybersecurity|cyber\s+security|defensive\s+security|protect|protection|stay\s+safe|password\s+safety|secure\s+my|prevent)\b/.test(
      value
    );

  if (intrusion && !defensiveSecurity) {
    return (
      "I can't teach students how to break into accounts, devices, networks, homes, " +
      "vehicles, locks, or other property. I can teach cybersecurity defense, " +
      "password safety, privacy, and protection instead."
    );
  }

  const abusiveSubject =
    /\b(bully|bullying|harass|harassment|stalk|stalking|humiliate|humiliation|blackmail|threaten|threat|abuse|abusive)\b/.test(
      value
    );

  const antiAbuse =
    /\b(report|reporting|prevent|prevention|stop\s+bullying|get\s+help|stay\s+safe|what\s+should\s+i\s+do)\b/.test(
      value
    );

  const harmfulSocial =
    /\b(write\s+(a\s+)?(message|text|post)|embarrass|humiliate|scare|intimidate|threaten|blackmail|hurt\s+(him|her|them|someone)|make\s+(him|her|them|someone)\s+(cry|afraid|scared))\b/.test(
      value
    );

  if (
    abusiveSubject &&
    (instructional || harmfulSocial) &&
    !antiAbuse
  ) {
    return (
      "I can't help someone bully, harass, threaten, stalk, humiliate, or abuse another person. " +
      "I can help with respectful communication, conflict resolution, reporting bullying, or staying safe."
    );
  }

  const violence =
    /\b(kill|murder|stab|shoot|poison|hurt\s+someone|hurt\s+(a\s+)?person|hurt\s+(an?\s+)?animal|torture)\b/.test(
      value
    );

  if (violence && instructional) {
    return (
      "I can't provide instructions for hurting a person or animal. " +
      "I can help with safety, prevention, conflict resolution, or another school-safe topic."
    );
  }

  const selfHarm =
    /\b(kill\s+myself|suicide|suicidal|self[- ]?harm|cut\s+myself|overdose\s+on)\b/.test(
      value
    );

  const methodRequest =
    /\b(how\s+to|best\s+way|easiest\s+way|method|methods|instructions|steps)\b/.test(
      value
    );

  if (selfHarm && methodRequest) {
    return (
      "I can't provide instructions for self-harm. If you or someone else may be in immediate danger, " +
      "tell a trusted adult, parent, teacher, counselor, or emergency service now."
    );
  }

  const crimeEvasion =
    /\b(hide\s+evidence|destroy\s+evidence|avoid\s+the\s+police|evade\s+police|get\s+away\s+with|cover\s+up\s+(a\s+)?crime|hide\s+stolen)\b/.test(
      value
    );

  if (crimeEvasion) {
    return (
      "I can't provide instructions that help commit or hide harmful or illegal activity. " +
      "I can help with safety, prevention, laws, or another school-safe topic."
    );
  }

  return null;
}

// === END AVAAIO EDUCATION TUTOR SAFETY BOUNDARY V1.1 ===


// === AVAAIO TUTOR SUBJECT BOUNDARY V1 ===

function detectTutorProblemTopic(raw: string): TutorTopic | null {
  const value = String(raw || "")
    .trim()
    .toLowerCase()
    .replace(/[−–—]/g, "-");

  if (!value) return null;

  // Writing requests must be detected conservatively.
  const writingMarkers = [
    "essay",
    "paragraph",
    "introduction",
    "conclusion",
    "thesis",
    "grammar",
    "sentence",
    "proofread",
    "revise",
    "revision",
    "writing prompt",
    "write about",
  ];

  if (writingMarkers.some((term) => value.includes(term))) {
    return "Essay Writing";
  }

  // Functions before ordinary arithmetic because functions can contain
  // + and - operators as part of an equation.
  if (
    /\bf\s*\(\s*x\s*\)\s*=/.test(value) ||
    /\bf\s*\(\s*-?\d+(?:\.\d+)?\s*\)/.test(value) ||
    /\by\s*=\s*-?\d*x(?:\s*[+\-]\s*\d+)?/.test(value) ||
    /\bfunction\b/.test(value) ||
    /\bsolve\s+for\s+x\b/.test(value)
  ) {
    return "Functions";
  }

  // Decimal arithmetic is its own practice category.
  if (
    /\d+\.\d+/.test(value) &&
    /[+\-×÷*/]/.test(value)
  ) {
    return "Decimals";
  }

  // Fraction notation before division detection.
  if (
    /\b\d+\s*\/\s*\d+\b/.test(value) &&
    (
      /[+\-]/.test(value) ||
      /\bfraction/.test(value) ||
      (
        value.match(/\b\d+\s*\/\s*\d+\b/g) || []
      ).length >= 1
    )
  ) {
    return "Fractions";
  }

  // Obvious arithmetic operators.
  const arithmetic = value.match(
    /-?\d+(?:\.\d+)?\s*([+\-×÷*/])\s*-?\d+(?:\.\d+)?/
  );

  if (arithmetic) {
    const op = arithmetic[1];

    if (op === "+") return "Addition";
    if (op === "-") return "Subtraction";
    if (op === "×" || op === "*") return "Multiplication";
    if (op === "÷" || op === "/") return "Division";
  }

  // Common word forms where no mathematical symbol was typed.
  if (/\b(add|addition|plus|sum)\b/.test(value)) {
    return "Addition";
  }

  if (/\b(subtract|subtraction|minus|difference)\b/.test(value)) {
    return "Subtraction";
  }

  if (/\b(multiply|multiplication|times|product)\b/.test(value)) {
    return "Multiplication";
  }

  if (/\b(divide|division|divided by|quotient)\b/.test(value)) {
    return "Division";
  }

  if (/\b(decimal|decimals)\b/.test(value)) {
    return "Decimals";
  }

  if (/\b(fraction|fractions|numerator|denominator)\b/.test(value)) {
    return "Fractions";
  }

  return null;
}

function tutorTopicBoundaryError(
  selectedTopic: TutorTopic,
  detectedTopic: TutorTopic | null
): string | null {
  if (!detectedTopic || detectedTopic === selectedTopic) {
    return null;
  }

  return (
    `That is a ${detectedTopic} problem. ` +
    `You're currently practicing ${selectedTopic}. ` +
    `Go to ${detectedTopic}, or enter a ${selectedTopic} problem here.`
  );
}

// === END AVAAIO TUTOR SUBJECT BOUNDARY V1 ===


function solveProblem(raw: string, topic: TutorTopic, grade: GradeBand): SolveResult {
  const safetyError = tutorEducationSafetyError(raw);

  if (safetyError) {
    return {
      ok: false,
      error: safetyError,
    };
  }

  const detectedTopic = detectTutorProblemTopic(raw);
  const boundaryError = tutorTopicBoundaryError(topic, detectedTopic);

  if (boundaryError) {
    return {
      ok: false,
      error: boundaryError,
    };
  }

  if (topic === "Decimals") return solveDecimalProblem(raw, grade);
  if (topic === "Essay Writing") return solveEssayProblem(raw, grade);
  if (topic === "Fractions") return solveFractionProblem(raw, grade);
  if (topic === "Functions") return solveFunctionProblem(raw, grade);

  const normalized = normalizeInput(raw, topic);
  if (!normalized) {
    return {
      ok: false,
      error: "Please type a math problem first, like 8 + 7 or 24 ÷ 6.",
    };
  }

  const match = normalized.match(/(-?\d+(?:\.\d+)?)\s*([+\-×÷])\s*(-?\d+(?:\.\d+)?)/);
  if (!match) {
    return {
      ok: false,
      error: "I could not read that problem yet. Try a format like 12 + 9, 18 - 4, 6 × 7, or 24 ÷ 6.",
    };
  }

  const a = safeNumber(match[1]);
  const op = match[2];
  const b = safeNumber(match[3]);

  if (a === null || b === null) {
    return {
      ok: false,
      error: "I found a number format I could not read. Please try again with plain numbers.",
    };
  }

  if (op === "÷" && b === 0) {
    return {
      ok: false,
      error: "Division by zero is not allowed. Try a different divisor.",
    };
  }

  const friendly = gradeFriendlyLine(grade);

  if (op === "+") {
    const answer = a + b;
    return {
      ok: true,
      normalized,
      answer,
      friendly,
      hint: "Try adding the larger place values first, then the smaller ones.",
      easier: `${Math.min(9, Math.abs(Math.trunc(a)))} + ${Math.min(9, Math.abs(Math.trunc(b)))}`,
      example: buildExample("Addition"),
      steps: [
        `Problem: ${a} + ${b}`,
        `Step 1: Add the numbers together.`,
        `${a} + ${b} = ${answer}`,
        `Answer: ${answer}`,
      ],
    };
  }

  if (op === "-") {
    const answer = a - b;
    return {
      ok: true,
      normalized,
      answer,
      friendly,
      hint: "Start with the first number and subtract in smaller chunks if that helps.",
      easier: `${Math.max(0, Math.min(20, Math.abs(Math.trunc(a))))} - ${Math.min(9, Math.abs(Math.trunc(b)))}`,
      example: buildExample("Subtraction"),
      steps: [
        `Problem: ${a} - ${b}`,
        `Step 1: Start with ${a}.`,
        `Step 2: Take away ${b}.`,
        `${a} - ${b} = ${answer}`,
        `Answer: ${answer}`,
      ],
    };
  }

  if (op === "×") {
    const answer = a * b;
    return {
      ok: true,
      normalized,
      answer,
      friendly,
      hint: "Think of multiplication as equal groups or repeated addition.",
      easier: `${Math.min(5, Math.max(1, Math.abs(Math.trunc(a))))} × ${Math.min(5, Math.max(1, Math.abs(Math.trunc(b))))}`,
      example: buildExample("Multiplication"),
      steps: [
        `Problem: ${a} × ${b}`,
        `Step 1: Think of ${a} groups of ${b}, or ${b} groups of ${a}.`,
        `Step 2: Multiply the numbers.`,
        `${a} × ${b} = ${answer}`,
        `Answer: ${answer}`,
      ],
    };
  }

  const answer = a / b;
  const whole = Number.isInteger(answer);
  return {
    ok: true,
    normalized,
    answer,
    friendly,
    hint: "Ask what number times the divisor gives the dividend.",
    easier: `${Math.max(2, Math.min(20, Math.abs(Math.trunc(a)) || 12))} ÷ ${Math.max(1, Math.min(5, Math.abs(Math.trunc(b)) || 2))}`,
    example: buildExample("Division"),
    steps: whole
      ? [
          `Problem: ${a} ÷ ${b}`,
          `Step 1: Ask how many groups of ${b} fit into ${a}.`,
          `Step 2: Check by multiplication.`,
          `${b} × ${answer} = ${a}`,
          `Answer: ${answer}`,
        ]
      : [
          `Problem: ${a} ÷ ${b}`,
          `Step 1: Divide ${a} by ${b}.`,
          `Step 2: This does not come out to a whole number.`,
          `${a} ÷ ${b} = ${answer}`,
          `Answer: ${answer}`,
        ],
  };
  return { ok: false, steps: [], answer: "" };
}

function buildPlaceholder(activeTopic: TutorTopic): string {
  switch (activeTopic) {
    case "Addition":
      return "Type an addition problem like 48 + 27";
    case "Subtraction":
      return "Type a subtraction problem like 42 - 17";
    case "Multiplication":
      return "Type a multiplication problem like 6 × 7";
    case "Division":
      return "Type a division problem like 24 ÷ 6";
    case "Fractions":
      return "Type a fractions problem like 1/2 + 1/4 or 3/4";
    case "Functions":
      return "Type a functions problem like f(x) = 2x + 3, f(4)";
  }

  return "";
}

export default function TutorPage({
  initialTopic = "Addition",
  lockTopic = false,
}: TutorPageProps) {
  const navigate = useNavigate();
  const { themeColors } = useContext(ThemeContext);
  const {
    pageStyle,
    shellStyle,
    panelStyle,
    cardStyle,
    controlStyle,
    buttonStyle,
  } = useMemo(() => makeTutorStyles(themeColors), [themeColors]);
  const [topic, setTopic] = useState<TutorTopic>(initialTopic);
  const [grade, setGrade] = useState<GradeBand>("K-2");
  const [question, setQuestion] = useState("");
  const [mode, setMode] = useState<"explain" | "hint" | "example" | "easy">("explain");
  const [response, setResponse] = useState(
    initialTopic === "Essay Writing"
      ? "Hello. I’m Ava. Share a paragraph, essay topic, or writing question and I will help you step by step."
      : "Hello. I’m Ava. Ask me a math question and I will help you step by step."
  );

  const solved = useMemo(() => solveProblem(question, topic, grade), [question, topic, grade]);

  function runExplain() {
    setMode("explain");
    if (!solved.ok) {
      setResponse(solved.error || "Please enter a problem first.");
      return;
    }
    setResponse(
      `${solved.friendly}\n\n${(solved.steps || []).join("\n")}\n\nYou are doing well. Keep working one step at a time.`
    );
  }

  function runHint() {
    setMode("hint");
    if (!solved.ok) {
      setResponse(solved.error || "Please enter a problem first.");
      return;
    }
    setResponse(
      `${solved.friendly}\n\nHint: ${solved.hint}\n\nTry solving it on your own first, and then I can help with the next step.`
    );
  }

  function runExample() {
    setMode("example");
    setResponse(
      `${gradeFriendlyLine(grade)}\n\n${buildExample(topic)}\n\nNow try your own problem and I will help you solve it.`
    );
  }

  function runEasier() {
    setMode("easy");
    if (!solved.ok) {
      if (topic === "Fractions") {
        setResponse("Let’s start with an easier fraction first. Try something like 1/2 + 1/2 or just 3/4.");
        return;
      }
      if (topic === "Functions") {
        setResponse("Let’s start with an easier function first. Try something like y = x + 2 or f(x) = x + 1, f(2).");
        return;
      }
      setResponse("Let’s start with an easier version first. Try something like 6 + 2 or 8 ÷ 2.");
      return;
    }
    setResponse(
      `Let’s make it easier first.\n\nTry this simpler problem:\n${solved.easier}\n\nOnce you solve that one, come back to your original problem.`
    );
  }

  function clearAll() {
    setQuestion("");
    setMode("explain");
    setResponse(
      topic === "Essay Writing"
        ? "Okay. We cleared the writing question. Type a new paragraph, essay topic, or writing question when you're ready."
        : "Okay. We cleared the problem. Type a new math question when you're ready."
    );
  }

  return (
    <div style={pageStyle}>
      <div style={shellStyle}>
        <div style={{ ...panelStyle, marginBottom: "14px" }}>
          <div
            style={{
              fontSize: "11px",
              letterSpacing: "0.22em",
              textTransform: "uppercase",
              color: themeColors.accent2,
              fontWeight: 800,
              marginBottom: "6px",
            }}
          >
            Ava Education
          </div>

          <h1
            style={{
              margin: 0,
              fontSize: "clamp(26px, 4.3vw, 48px)",
              lineHeight: 1,
              fontWeight: 900,
              color: themeColors.accent,
              textShadow: themeColors.glow,
              wordBreak: "break-word",
            }}
          >
            {lockTopic ? `${topic} Tutor` : "Ava Tutor"}
          </h1>

          <p
            style={{
              margin: "8px 0 0",
              fontSize: "clamp(12px, 1.5vw, 16px)",
              color: themeColors.mutedText,
              maxWidth: "860px",
              lineHeight: 1.4,
            }}
          >
            {topic === "Essay Writing"
              ? "Ask a writing question and Ava will help with structure, grammar, introductions, conclusions, and revisions."
              : "Ask a math question and Ava will explain it clearly, patiently, and step by step."}
          </p>
        </div>

        <div
          className="tutor-layout"
          style={{
            display: "grid",
            gridTemplateColumns: "minmax(0, 1.45fr) minmax(300px, 0.95fr)",
            gap: "14px",
            alignItems: "start",
          }}
        >
          <div style={cardStyle}>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                gap: "10px",
                marginBottom: "12px",
              }}
            >
              {lockTopic ? (
                <div>
                  <div style={{ marginBottom: "6px", fontWeight: 800, fontSize: "13px" }}>
                    Subject
                  </div>
                  <div
                    style={{
                      ...controlStyle,
                      display: "flex",
                      alignItems: "center",
                      minHeight: "48px",
                      fontWeight: 700,
                    }}
                  >
                    {topic}
                  </div>
                </div>
              ) : (
                <div>
                  <div style={{ marginBottom: "6px", fontWeight: 800, fontSize: "13px" }}>
                    Topic
                  </div>
                  <select
                    value={topic}
                    onChange={(e) => setTopic(e.target.value as TutorTopic)}
                    style={controlStyle}
                  >
                    <option value="Addition">Addition</option>
                    <option value="Subtraction">Subtraction</option>
                    <option value="Multiplication">Multiplication</option>
                    <option value="Division">Division</option>
                    <option value="Fractions">Fractions</option>
                    <option value="Functions">Functions</option>
                    <option value="Decimals">Decimals</option>
                    <option value="Essay Writing">Essay Writing</option>
                  </select>
                </div>
              )}

              <div>
                <div style={{ marginBottom: "6px", fontWeight: 800, fontSize: "13px" }}>
                  Grade Level
                </div>
                <select
                  value={grade}
                  onChange={(e) => setGrade(e.target.value as GradeBand)}
                  style={controlStyle}
                >
                  <option value="K-2">K–2</option>
                  <option value="3-5">3–5</option>
                  <option value="6-8">6–8</option>
                  <option value="9-12">9–12</option>
                </select>
              </div>
            </div>

            <div style={{ ...panelStyle, marginBottom: "12px", padding: "14px 12px" }}>
              <div
                style={{
                  fontSize: "11px",
                  color: themeColors.mutedText,
                  letterSpacing: "0.18em",
                  textTransform: "uppercase",
                  marginBottom: "8px",
                  fontWeight: 700,
                }}
              >
                Student Problem
              </div>

              <textarea
                value={question}
                onChange={(e) => setQuestion(e.target.value)}
                placeholder={
                  lockTopic
                    ? buildPlaceholder(topic)
                    : "Type a problem like 48 + 27, 42 - 17, 6 × 7, 24 ÷ 6, 1/2 + 1/4, or f(x) = 2x + 3, f(4)"
                }
                style={{
                  ...controlStyle,
                  minHeight: "110px",
                  resize: "vertical",
                  fontSize: "16px",
                  fontFamily: "inherit",
                }}
              />
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(145px, 1fr))",
                gap: "8px",
              }}
            >
              <button type="button" onClick={runExplain} style={buttonStyle}>
                Explain Step by Step
              </button>
              <button type="button" onClick={runHint} style={buttonStyle}>
                Give Hint
              </button>
              <button type="button" onClick={runExample} style={buttonStyle}>
                Show Example
              </button>
              <button type="button" onClick={runEasier} style={buttonStyle}>
                Try Easier Version
              </button>
              <button type="button" onClick={clearAll} style={buttonStyle}>
                Clear
              </button>
            </div>

            <div
              style={{
                marginTop: "12px",
                padding: "10px 12px",
                borderRadius: "12px",
                background: "rgba(255,255,255,0.03)",
                border: `1px solid ${themeColors.border}`,
              }}
            >
              <div style={{ fontWeight: 800, marginBottom: "4px", fontSize: "13px" }}>
                Current Mode
              </div>
              <div
                style={{
                  color: themeColors.mutedText,
                  lineHeight: 1.4,
                  fontSize: "13px",
                }}
              >
                {mode === "explain" && "Step-by-step explanation"}
                {mode === "hint" && "Hint mode"}
                {mode === "example" && "Example mode"}
                {mode === "easy" && "Easier version mode"}
              </div>
            </div>
          </div>

          <div style={{ display: "grid", gap: "12px" }}>
            <div style={cardStyle}>
              <div style={{ fontSize: "16px", fontWeight: 900, marginBottom: "6px" }}>
                Ava Response
              </div>
              <div
                style={{
                  lineHeight: 1.6,
                  color: themeColors.mutedText,
                  whiteSpace: "pre-wrap",
                  fontSize: "14px",
                }}
              >
                {response}
              </div>
            </div>

            {solved.ok && (
              <div style={cardStyle}>
                <div style={{ fontSize: "16px", fontWeight: 900, marginBottom: "6px" }}>
                  Quick Read
                </div>
                <div
                  style={{
                    lineHeight: 1.55,
                    color: themeColors.mutedText,
                    fontSize: "14px",
                  }}
                >
                  Problem: <strong>{solved.normalized}</strong>
                  <br />
                  Answer: <strong>{String(solved.answer)}</strong>
                </div>
              </div>
            )}

            <div style={cardStyle}>
              <div style={{ fontSize: "16px", fontWeight: 900, marginBottom: "6px" }}>
                Navigation
              </div>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr",
                  gap: "8px",
                }}
              >
                <button type="button" onClick={() => navigate("/education")} style={buttonStyle}>
                  Back to Education
                </button>
                <button
                  type="button"
                  onClick={() => navigate("/education/flashcards")}
                  style={buttonStyle}
                >
                  Go to Flash Cards
                </button>
                <button
                  type="button"
                  onClick={() => navigate("/education/mathboard")}
                  style={buttonStyle}
                >
                  Go to Math Board
                </button>
                <button type="button" onClick={() => navigate("/games")} style={buttonStyle}>
                  Back to Games
                </button>
              </div>
            </div>
          </div>
        </div>

        <style>{`
          @media (max-width: 1120px) {
            .tutor-layout {
              grid-template-columns: 1fr !important;
            }
          }
        `}</style>
      </div>
    </div>
  );
}